/**
 * S2 Task 6 — Widget channel events.
 *
 * `widget.message.ingress` is published by the ws-gateway /widget namespace
 * when a visitor sends a message. The worker Ingestor consumer picks this
 * up and persists it as a conversation message.
 */
import { z, uuidSchema } from '@kc3/validation';
import { baseEventFields } from './_base.js';
export const WidgetMessageIngressEventSchema = z.object({
    type: z.literal('widget.message.ingress'),
    ...baseEventFields,
    visitorId: z.string().min(1),
    widgetKey: z.string().min(1),
    conversationId: uuidSchema.nullable(),
    content: z.string().min(1).max(4096),
    attachmentIds: z.array(uuidSchema).optional(),
    timestamp: z.string().datetime(),
});
/**
 * bot.turn.reply — published by BotOrchestrator (apps/worker) on every
 * non-escalating bot turn (FAQ menu shown, or an FAQ article answer given).
 * ws-gateway's widget-bridge consumer relays this to the visitor's
 * /widget socket room as `widget:bot:reply`. Unlike widget.message.ingress
 * (visitor -> server) this event carries visitorId directly since no
 * conversation exists yet at this point — no Redis lookup is needed to
 * route it, unlike conversation.message.new's escalation-path relay.
 */
export const WidgetBotTurnReplyEventSchema = z.object({
    type: z.literal('bot.turn.reply'),
    ...baseEventFields,
    visitorId: z.string().min(1),
    message: z.string(),
    options: z.array(z.object({ id: z.string().min(1), label: z.string().min(1) })).optional(),
});
/**
 * widget.escalation.assigned — published by BotOrchestrator.escalate()
 * (apps/worker) the moment a human agent is successfully assigned to a
 * freshly-escalated chat conversation — BEFORE that agent has sent any
 * message. Distinct from conversation.message.new (which only fires once
 * a real message exists): this event lets the visitor's widget show
 * "An agent has joined — you can chat now" immediately at assignment time,
 * rather than waiting for the agent's first reply. Not published when no
 * agent could be assigned (assignChatConversation returned null) — in that
 * case the widget's own client-side timeout is what surfaces the
 * "all agents are busy" fallback; the conversation stays queued
 * server-side and this event still fires later if a human manually claims
 * it, so the widget can flip to live-chat even after showing that message.
 */
export const WidgetEscalationAssignedEventSchema = z.object({
    type: z.literal('widget.escalation.assigned'),
    ...baseEventFields,
    visitorId: z.string().min(1),
    sessionId: uuidSchema,
    assignedAgentId: uuidSchema,
});
export const HandoffChatChannelSchema = z.enum(['whatsapp', 'facebook', 'instagram', 'telegram']);
/**
 * chat.offer.created — published by BotOrchestrator.escalate() (apps/worker)
 * each time a candidate agent is offered an escalated chat conversation,
 * BEFORE that agent has accepted or declined. Distinct from
 * widget.escalation.assigned (which only fires once an agent has actually
 * accepted) — this event drives the agent-side "New chat request" popup.
 * `conversationAttemptId` identifies this ESCALATION ATTEMPT (stable
 * across every agent offered during the retry loop for one visitor's
 * single "Talk to an Agent" click), separate from `offerId` (unique per
 * individual agent offer — a new offerId is minted for each retry).
 * `transcript` is the bot conversation history so far, included so the
 * agent has context before accepting (approved design: "include the bot
 * conversation summary").
 */
export const ChatOfferCreatedEventSchema = z.object({
    type: z.literal('chat.offer.created'),
    ...baseEventFields,
    offerId: uuidSchema,
    visitorId: z.string().min(1),
    conversationAttemptId: uuidSchema,
    agentUserId: uuidSchema,
    transcript: z.array(z.object({ from: z.enum(['bot', 'visitor']), text: z.string() })),
    expiresAt: z.string().datetime(),
    visitorEmail: z.string().nullable().optional(),
    visitorName: z.string().nullable().optional(),
    reassign: z.boolean().optional(),
    /** If set, the offer reassigns a finalized conversation (conversations.id). */
    conversationId: uuidSchema.optional(),
    /** If set, the offer is for a WhatsApp/Facebook/Instagram chat handed back by the AI. */
    channelChat: z.object({ channel: HandoffChatChannelSchema, chatId: uuidSchema }).optional(),
});
/** chat.handoff.requested — the AI could not answer an AI-assigned channel chat. */
export const ChatHandoffRequestedEventSchema = z.object({
    type: z.literal('chat.handoff.requested'),
    ...baseEventFields,
    channel: HandoffChatChannelSchema,
    chatId: uuidSchema,
    senderHandle: z.string().min(1),
    customerName: z.string().nullable(),
    lastMessage: z.string(),
});
/** chat.handoff.accepted — an agent accepted a handed-off channel chat. */
export const ChatHandoffAcceptedEventSchema = z.object({
    type: z.literal('chat.handoff.accepted'),
    ...baseEventFields,
    channel: HandoffChatChannelSchema,
    chatId: uuidSchema,
    assignedUserId: uuidSchema,
});
/**
 * chat.offer.responded — inserted into the outbox by
 * POST /agent/chat-offers/:offerId/respond (apps/api) when an agent
 * clicks Accept or Decline. The worker's chat-offer-response consumer
 * (Task 4) reacts to this independently of its own 15s offer timer —
 * whichever resolves the offer first (a real response, or the timeout)
 * wins; both paths go through the same idempotency guard (Task 3) so a
 * late-arriving duplicate is a safe no-op.
 */
export const ChatOfferRespondedEventSchema = z.object({
    type: z.literal('chat.offer.responded'),
    ...baseEventFields,
    offerId: uuidSchema,
    response: z.enum(['accepted', 'declined']),
});
/**
 * live.chat.ai.handback — published by the worker when the AI answering a
 * website live chat (after "Transfer to AI") has no answer. The chat's
 * `ai_assigned` flag has already been cleared; the orchestrator-side consumer
 * offers the chat to the next available agent.
 */
export const LiveChatAiHandbackEventSchema = z.object({
    type: z.literal('live.chat.ai.handback'),
    ...baseEventFields,
    liveChatId: uuidSchema,
});
/**
 * widget.typing — the AI is composing a reply in a website live chat. Relayed by
 * the ws-gateway to that chat's visitor as widget:typing:start / :stop. The
 * visitor widget previously only knew about human-agent typing, so an AI reply
 * arrived with no "typing" cue at all.
 */
export const WidgetTypingEventSchema = z.object({
    type: z.literal('widget.typing'),
    ...baseEventFields,
    liveChatId: uuidSchema,
    actorName: z.string().min(1),
    state: z.enum(['start', 'stop']),
});
/** chat.typing — someone (the AI or an agent) started/stopped typing in a channel chat. */
export const ChannelTypingEventSchema = z.object({
    type: z.literal('chat.typing'),
    ...baseEventFields,
    channel: HandoffChatChannelSchema,
    chatId: uuidSchema,
    actor: z.enum(['ai', 'agent']),
    actorName: z.string().min(1),
    actorUserId: uuidSchema.nullable(),
    state: z.enum(['start', 'stop']),
});
//# sourceMappingURL=widget.js.map